import { BrButton, BrLoading } from '@govbr-ds/react-components';
import React, { useContext, useEffect, useState } from 'react';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { Button, Modal } from 'react-dsgov';
import { useNotificacao } from '../../../../../hooks/useNotificacao';
import { ETabTitularArrendatario, IEstadoTitularArrendatario } from '../../../lib/titularArrendatarioEstadoProcesso';
import { useNavigate } from 'react-router-dom';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { listarErrosValidacao } from '../../../../../utils/formatErrosValidacao';
import styles from './botoesAcao.module.scss';
import { IDeclaracaoTitularOuArrendatario } from '../../../../../interfaces/IDeclaracoes';
import { ERegimesTributacao, ERegimesTributacaoLabels } from '../../../../../enums/ERegimesTributacao';

interface IBotoesAcaoProps {
  isEdicaoOutrosRegimes: boolean;
  isRetificadora: boolean;
  declaracaoId: string;
  estadoTela: IEstadoTitularArrendatario | null;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  isNovaOperacaoPLG: boolean;
  setIsModalEnviarDeclaracaoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeclaranteDataLoading: boolean;
  isDeclaracaoDataLoading: boolean;
  regimeTributacao: ERegimesTributacao | null;
  hasDeclaracaoInicial: boolean;
}

const BotoesAcao = ({
  isEdicaoOutrosRegimes,
  isRetificadora,
  declaracaoId,
  estadoTela,
  step,
  setStep,
  isNovaOperacaoPLG,
  setIsModalEnviarDeclaracaoOpen,
  isDeclaranteDataLoading,
  isDeclaracaoDataLoading,
  regimeTributacao,
  hasDeclaracaoInicial,
}: IBotoesAcaoProps) => {
  const [isModalExcluirRetificacaoOpen, setIsModalExcluirRetificacaoOpen] = useState(false);
  const [isLoadingValidacao, setIsLoadingValidacao] = useState(false);

  const [isOperacoesPLGValidas, setIsOperacoesPLGValidas] = useState(false);

  const [isLoadingDeclaracaoInicial, setIsLoadingDeclaracaoInicial] = useState(false);

  const mostrarNotificacao = useNotificacao();

  const navigate = useNavigate();

  const context = useContext(DiefCfemContext);

  const declaracaoTitularArrendatario = context?.declaracaoTitularArrendatario;

  const {
    getDeclaracaoResumoPLGById,
    deleteDeclaracaoById,
    postCadastrarDeclaracaoInicial,
    putCadastrarDeclaracaoInicial,
  } = useBackEndRequests();

  useEffect(() => {
    if (
      declaracaoId &&
      (step === 1 || step === 2) &&
      !isNovaOperacaoPLG &&
      context?.declaracaoTitularArrendatario?.operacoes
    ) {
      setIsOperacoesPLGValidas(false);
    }
  }, [declaracaoId, step, isNovaOperacaoPLG, context?.declaracaoTitularArrendatario?.operacoes]);

  const validarOperacoes = async () => {
    setIsLoadingValidacao(true);
    if (!declaracaoId) {
      mostrarNotificacao({
        tipo: 'warning',
        conteudo: 'Erro ao validar operações, tente novamente mais tarde.',
      });
      setIsLoadingValidacao(false);
      return;
    }

    if (estadoTela?.telaSelecionada === ETabTitularArrendatario.OUTROS_REGIMES_CONFIGURACAO) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Não é possível validar operações nesta etapa',
      });
      setIsLoadingValidacao(false);
      return;
    }

    await getDeclaracaoResumoPLGById(declaracaoId)
      .then(() => {
        setIsOperacoesPLGValidas(true);
        setStep(step + 1);
        context?.setErrosValidacaoDeclaracao({ tabNContent: [], showContainer: false });
      })
      .catch((err) => {
        if (err.response.status === 422) {
          const tabNContent = listarErrosValidacao(err.response.data.detail);
          context?.setErrosValidacaoDeclaracao({
            tabNContent: tabNContent,
            showContainer: true,
          });
          mostrarNotificacao({
            tipo: 'warning',
            conteudo: 'Itens da declaração inválidos. Verifique a caixa de notificações na parte superior da página.',
          });
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao finalizar declaração, tente novamente mais tarde.',
            ...(err && { complementoSentryId: err }),
          });
        }
        setIsOperacoesPLGValidas(false);
      });

    setIsLoadingValidacao(false);
  };

  const excluirRetificacao = async () => {
    await deleteDeclaracaoById(declaracaoId)
      .then(() => {
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Retificação excluída com sucesso',
          });
        }, 100);

        window.scrollTo(0, 0);
        navigate(`/titular-ou-arrendatario`);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao excluir a retificação, tente novamente mais tarde.',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  const cadastrarDeclaracaoInicial = async () => {
    setIsLoadingDeclaracaoInicial(true);
    if (regimeTributacao === null) {
      mostrarNotificacao({
        conteudo: 'Selecione o regime de tributação para continuar.',
        tipo: 'danger',
      });
      setIsLoadingDeclaracaoInicial(false);
      return;
    }
    if (!hasDeclaracaoInicial) {
      await postCadastrarDeclaracaoInicial({
        ...(declaracaoTitularArrendatario as IDeclaracaoTitularOuArrendatario),
        regimeTributacao: {
          id: regimeTributacao,
          nome: ERegimesTributacaoLabels[regimeTributacao as ERegimesTributacao]?.toString(),
        },
      })
        .then(() => {
          setIsLoadingDeclaracaoInicial(false);
          mostrarNotificacao({
            conteudo: 'Tudo certo! Sua declaração já está em andamento.',
            tipo: 'success',
          });
          setStep(step + 1);
        })
        .catch((err) => {
          setIsLoadingDeclaracaoInicial(false);
          mostrarNotificacao({
            conteudo: 'Erro ao cadastrar a declaração inicial, tente novamente mais tarde.',
            tipo: 'danger',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else {
      setIsLoadingDeclaracaoInicial(true);
      await putCadastrarDeclaracaoInicial({
        id: declaracaoId,
        regimeTributacao: {
          id: regimeTributacao,
          nome: ERegimesTributacao[regimeTributacao].toString(),
        },
      })
        .then(() => {
          setIsLoadingDeclaracaoInicial(false);
          setStep(step + 1);
        })
        .catch((err) => {
          if (err.status === 400) {
            mostrarNotificacao({
              conteudo: err.response.data.message ?? 'Erro ao editar a declaração inicial, tente novamente mais tarde.',
              tipo: 'danger',
            });
          } else {
            mostrarNotificacao({
              conteudo: 'Erro ao editar a declaração inicial, tente novamente mais tarde.',
              tipo: 'danger',
              ...(err && { complementoSentryId: err }),
            });
          }
          setIsLoadingDeclaracaoInicial(false);
        });
    }
  };

  const stepButton = (
    onClick: () => void,
    icon: string,
    text: string,
    isPrimary: boolean = false,
    isDisabled: boolean = false,
    isLoading: boolean = false
  ) => {
    return (
      <>
        {isLoading ? (
          <BrLoading />
        ) : (
          <BrButton
            secondary={!isPrimary}
            onClick={onClick}
            primary={isPrimary}
            disabled={isDisabled}
            data-testid={`btn-${text.toLowerCase()}-test`}
          >
            <span className={styles.button}>
              <i className={icon} /> {text}
            </span>
          </BrButton>
        )}
      </>
    );
  };

  const botaoRetornar = () => {
    if (step !== 0 && !isNovaOperacaoPLG) {
      return stepButton(() => setStep(step - 1), `fas fa-arrow-left`, 'Voltar');
    } else if (!isNovaOperacaoPLG) {
      return stepButton(() => navigate('/titular-ou-arrendatario'), `fas fa-times`, 'Desistir');
    }
    return null;
  };

  const botaoAvancar = () => {
    if (step < 3 && !isNovaOperacaoPLG) {
      if (step === 0) {
        return stepButton(
          cadastrarDeclaracaoInicial,
          `fas fa-arrow-right`,
          'Avançar',
          true,
          false,
          isLoadingDeclaracaoInicial
        );
      }
      if (step === 2 && !isOperacoesPLGValidas) {
        return stepButton(
          validarOperacoes,
          `fas fa-check`,
          'Finalizar',
          true,
          false,
          isDeclaranteDataLoading || isDeclaracaoDataLoading || isLoadingValidacao
        );
      } else {
        return stepButton(
          () => {
            setStep(step + 1);
          },
          `fas fa-arrow-right`,
          'Avançar',
          true,
          false,
          isDeclaranteDataLoading || isDeclaracaoDataLoading
        );
      }
    } else if (!isNovaOperacaoPLG) {
      return stepButton(() => setIsModalEnviarDeclaracaoOpen(true), `fas fa-paper-plane`, 'Enviar', true);
    }
    return null;
  };

  return (
    <>
      <Modal title='Deseja realmente excluir a retificação?' useScrim modalOpened={isModalExcluirRetificacaoOpen}>
        <Modal.Body>As alterações realizadas nesta declaração serão perdidas.</Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={() => setIsModalExcluirRetificacaoOpen(false)}>
            Cancelar
          </Button>
          <Button primary small m={2} onClick={() => excluirRetificacao()}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>
      {isEdicaoOutrosRegimes === false && isRetificadora && (
        <div className={styles.containerDelete}>
          <BrButton icon='trash' secondary onClick={() => setIsModalExcluirRetificacaoOpen(true)}>
            Excluir Retificação
          </BrButton>
        </div>
      )}
      {!isEdicaoOutrosRegimes && (
        <div className={styles.containerStep}>
          {botaoRetornar()}
          {botaoAvancar()}
        </div>
      )}
    </>
  );
};

export default BotoesAcao;
